/** Ported from chief's `web/src/toast.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Icon, type IconName } from './Icon.tsx';
import { Link } from './router.tsx';

/**
 * Transient confirmation of an action ("Production started", "Deleted launch-teaser").
 *
 * The pages used to put every outcome in a notice paragraph at the top, which
 * meant scrolling up to find out whether the button you just pressed worked.
 * A toast appears where the eye already is and leaves on its own; anything
 * that has to stay readable — git's stderr, a parse error — is still rendered
 * inline by the page that owns it.
 */

export type ToastKind = 'ok' | 'error' | 'info' | 'warn';

interface Toast {
  readonly id: number;
  readonly kind: ToastKind;
  readonly text: string;
  readonly link: ToastLink | undefined;
}

/**
 * Where a toast leads when its text is a link: "Content launch-teaser is being
 * planned" → the content; or a button after the text that takes the action
 * back: "Declined “Launch teaser”" · **Undo**.
 */
export type ToastLink =
  | { readonly href: string }
  | { readonly label: string; readonly onClick: () => void };

export interface Toaster {
  push(kind: ToastKind, text: string, link?: ToastLink): void;
  ok(text: string, link?: ToastLink): void;
  error(text: string): void;
  info(text: string): void;
  warn(text: string): void;
}

const Context = createContext<Toaster | null>(null);

const ICONS: Record<ToastKind, IconName> = {
  ok: 'check-circle',
  error: 'x-circle',
  info: 'info',
  warn: 'alert',
};

/** Errors stay longer: they are the ones that have to be read. */
const LIFETIME_MS: Record<ToastKind, number> = { ok: 5000, info: 6000, warn: 8000, error: 10000 };

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string, link?: ToastLink): void => {
      const id = next.current;
      next.current += 1;
      setToasts((current) => [...current.slice(-3), { id, kind, text, link }]);
      window.setTimeout(() => dismiss(id), LIFETIME_MS[kind]);
    },
    [dismiss],
  );

  const toaster = useMemo<Toaster>(
    () => ({
      push,
      ok: (text, link) => push('ok', text, link),
      error: (text) => push('error', text),
      info: (text) => push('info', text),
      warn: (text) => push('warn', text),
    }),
    [push],
  );

  return (
    <Context.Provider value={toaster}>
      {children}
      <div className="toasts" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <div className={`toast toast--${toast.kind}`} key={toast.id} role={toast.kind === 'error' ? 'alert' : 'status'}>
            <Icon name={ICONS[toast.kind]} />
            <span className="toast__text">
              {toast.link === undefined ? (
                toast.text
              ) : 'href' in toast.link ? (
                <Link className="link" href={toast.link.href} onClick={() => dismiss(toast.id)}>
                  {toast.text}
                </Link>
              ) : (
                <ToastAction text={toast.text} link={toast.link} onDismiss={() => dismiss(toast.id)} />
              )}
            </span>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
            >
              <Icon name="x" />
            </button>
          </div>
        ))}
      </div>
    </Context.Provider>
  );
}

/** The text, then a button that dismisses the toast and runs its action. */
function ToastAction({
  text,
  link,
  onDismiss,
}: {
  readonly text: string;
  readonly link: { readonly label: string; readonly onClick: () => void };
  readonly onDismiss: () => void;
}) {
  return (
    <>
      {text}{' '}
      <button
        type="button"
        className="link link--button"
        onClick={() => {
          onDismiss();
          link.onClick();
        }}
      >
        {link.label}
      </button>
    </>
  );
}

export function useToast(): Toaster {
  const value = useContext(Context);
  if (value === null) throw new Error('useToast() outside <ToastProvider>');
  return value;
}
